import {
  FC,
  ReactNode,
  SyntheticEvent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import { useScreenSizes } from '@hooks/useScreenSizes';
import { useWindowDimensions } from '@hooks/useWindowDimensions';
import { useUserDataById } from '@redux/selectors/users';
import { VerificationRole } from '@utils/typings/user';
import { PremierText, DefaultText } from '@components/text';
import UserScore from '@components/userScore';
import ImagePlaceholder from '@components/loader/ImagePlaceholder';
import verifiedProIcon from '@assets/icons/verified-pro.png';
import verifiedUserIcon from '@assets/icons/verified-user.png';
import defaultBackground from '@assets/images/background.png';
import defaultAvatar from '@assets/images/avatar.png';
import { theme } from 'theme';

import Statistics from './Statistics';
import CountryFlag from './CountryFlag';

const Container = styled.div`
  height: 100%;
  width: 40%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
  }
`;

const ImageBackground = styled.div<{
  $backgroundImage: string;
  $pageHeight?: number;
  $pageWidth?: number;
}>`
  position: relative;
  align-self: center;
  overflow: hidden;
  aspect-ratio: 2/3;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-image: url(${({ $backgroundImage }) => $backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const HiddenImage = styled.img`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const ProfileDataContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
`;

const ProfileImageContainer = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: 100%;
  }
`;

const ProfileImage = styled.img<{ $scale: number; $isLoaded?: boolean }>`
  height: 100%;
  align-self: flex-end;
  transform: scale(${({ $scale }) => $scale});
  object-fit: contain;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0.1)};
  transition: opacity 0.3s ease-in-out;
`;

const InfoContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  top: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: 100%;
  }
`;

const Gradient = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  right: 2px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  background: ${({ theme }) =>
    `linear-gradient(to bottom, transparent 30%, ${theme.colors.black}60 60%, ${theme.colors.black})`};
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 3px;
    background: radial-gradient(
      ellipse at bottom center,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 40%,
      rgba(255, 255, 255, 0) 80%
    );

    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: 1;
    pointer-events: none;
  }
`;

const TopInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 16px;
`;

const BottomInfoContainer = styled.div`
  padding: 16px;
  z-index: 1;
`;

const NameWithIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const NameContainer = styled.div`
  flex: 0 1 auto;
`;

const IconContainer = styled.div`
  width: 20px;
  height: 20px;
  margin-left: 4px;
`;

const VerifiedIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const Name = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.white};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Bio = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[300]};
  margin: 8px 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

const LinearGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: ${({ theme }) =>
    `linear-gradient(to bottom, transparent 30%, ${theme.colors.black}${theme.opacity[40]} 60%, ${theme.colors.black})`};
  pointer-events: none;
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  padding: 0 16px;
`;

type Props = {
  userId: string;
  canGoBack?: boolean;
  renderActions?: () => ReactNode;
};

const ProfileInfo: FC<Props> = ({ userId, renderActions }) => {
  const profile = useUserDataById(userId);
  const [avatar, setAvatar] = useState<string>(() =>
    profile?.image ? profile.image : defaultAvatar,
  );
  const [background, setBackground] = useState<string>(() =>
    profile?.background ? profile.background : defaultBackground,
  );
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState<boolean>(false);
  const { isTablet, isMobile } = useScreenSizes();
  const { width, height } = useWindowDimensions();

  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.naturalWidth === 0) {
      setAvatar(defaultAvatar);
    }
    setIsImageLoaded(true);
  }, []);

  const onImageError = useCallback(() => {
    setAvatar(defaultAvatar);
    setIsImageLoaded(true);
  }, []);

  const onBackgroundLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      if (e.currentTarget.naturalWidth === 0) {
        setBackground(defaultBackground);
      }
      setIsBackgroundLoaded(true);
    },
    [],
  );

  const onBackgroundError = useCallback(() => {
    setBackground(defaultBackground);
    setIsBackgroundLoaded(true);
  }, []);

  const renderIcon = useCallback((verifiedAs: VerificationRole) => {
    switch (verifiedAs) {
      case VerificationRole.Pro:
        return <VerifiedIcon src={verifiedProIcon} />;
      case VerificationRole.User:
        return <VerifiedIcon src={verifiedUserIcon} />;
      default:
        return null;
    }
  }, []);

  useEffect(() => {
    setAvatar(profile?.image || defaultAvatar);
  }, [profile?.image]);

  useEffect(() => {
    setBackground(profile?.background || defaultBackground);
  }, [profile?.background]);

  return (
    <Container>
      <ImageBackground
        $backgroundImage={background}
        $pageWidth={isMobile || isTablet ? width : undefined}
        $pageHeight={isMobile || isTablet ? height : undefined}
      >
        {(!isImageLoaded || !isBackgroundLoaded) && <ImagePlaceholder />}

        <HiddenImage
          src={background}
          alt='background'
          onLoad={onBackgroundLoad}
          onError={onBackgroundError}
          loading='lazy'
        />

        <ProfileDataContainer>
          <ProfileImageContainer>
            <ProfileImage
              src={avatar}
              alt={profile?.name || 'Profile'}
              $scale={1}
              $isLoaded={isImageLoaded}
              onLoad={onImageLoad}
              onError={onImageError}
              loading='lazy'
            />
          </ProfileImageContainer>

          <InfoContainer>
            <Gradient />

            <TopInfoContainer>
              {profile?.country ? (
                <CountryFlag country={profile.country} />
              ) : (
                <div />
              )}

              {profile?.score != null && (
                <UserScore
                  userId={userId}
                  value={profile.score}
                  size={theme.fonts.fontSize['5xl']}
                />
              )}
            </TopInfoContainer>

            <BottomInfoContainer>
              <NameWithIconContainer>
                <NameContainer>
                  <Name>{profile?.name}</Name>
                </NameContainer>

                {profile?.verifiedAs && (
                  <IconContainer>
                    {renderIcon(profile?.verifiedAs)}
                  </IconContainer>
                )}
              </NameWithIconContainer>

              <Bio>{profile?.bio || ''}</Bio>
            </BottomInfoContainer>
          </InfoContainer>
        </ProfileDataContainer>

        <LinearGradient />

        <StatsContainer>
          <ButtonsContainer>
            {renderActions && renderActions()}
          </ButtonsContainer>

          <Statistics userId={userId} />
        </StatsContainer>
      </ImageBackground>
    </Container>
  );
};

export default memo(ProfileInfo);
